import type { CustomFormResult, ReportingWebhookResult } from '@/services/auth/types';
import type { CustomForm, FacebookLeadAdsForm, ReportingWebhook } from '@customerio/hydra-ui-api';
import { CUSTOM_FORM_TYPE_LABELS, FACEBOOK_LEAD_ADS_FORM_TYPE } from '../constants';
import { generatePath } from '@/services/router/generate-path';
import { filterBySearchTerms } from './search-filter';

// ─── Custom Forms ─────────────────────────────────────────────────────────────

function formDetailsUrl(workspaceId: number, type: string, formId: number | string): string {
  const path = generatePath('/workspaces/:workspaceId/integrations/forms/:type/:formId', { workspaceId, type, formId });
  return `${window.location.origin}${path}`;
}

/**
 * Maps a CustomForm to a CustomFormResult.
 * The edit URL is the form's details page, `integrations/forms/:type/:id`.
 */
export function mapCustomFormToSearchResult(form: CustomForm, workspaceId: number): CustomFormResult {
  return {
    kind: 'custom_form',
    doc_id: `custom_form-${form.id}`,
    title: form.name,
    url: formDetailsUrl(workspaceId, form.form_type, form.id),
    other: {
      env_id: workspaceId,
      form_type: form.form_type,
      form_id: form.form_id,
      status: form.status,
      created_at: form.created_at > 0 ? form.created_at * 1000 : undefined,
      last_activity_at: form.last_activity_at > 0 ? form.last_activity_at * 1000 : undefined,
      html_form_url: form.html_form_url.length > 0 ? form.html_form_url : undefined,
    },
  };
}

export function mapFacebookLeadAdsFormToSearchResult(form: FacebookLeadAdsForm, workspaceId: number): CustomFormResult {
  return {
    kind: 'custom_form',
    doc_id: `${FACEBOOK_LEAD_ADS_FORM_TYPE}_form-${form.id}`,
    title: form.facebook_form_name,
    url: formDetailsUrl(workspaceId, 'facebook', form.id),
    other: {
      env_id: workspaceId,
      form_type: FACEBOOK_LEAD_ADS_FORM_TYPE,
      form_id: form.facebook_form_id,
      status: form.status,
      created_at: form.created_at > 0 ? form.created_at * 1000 : undefined,
      last_activity_at: form.last_activity_at > 0 ? form.last_activity_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for Facebook Lead Ads forms. Matches against name, form type slug, and configured label.
 */
export function filterFacebookLeadAdsForms(
  forms: FacebookLeadAdsForm[],
  query: string,
  cap: number,
): FacebookLeadAdsForm[] {
  const displayLabel = CUSTOM_FORM_TYPE_LABELS[FACEBOOK_LEAD_ADS_FORM_TYPE];
  return filterBySearchTerms(forms, query, {
    cap,
    getTerms: (form) => [FACEBOOK_LEAD_ADS_FORM_TYPE, displayLabel, form.facebook_form_name],
  });
}

/**
 * Client-side filter for custom forms. Matches against name, form_type slug, and configured label.
 */
export function filterCustomForms(forms: CustomForm[], query: string, cap: number): CustomForm[] {
  return filterBySearchTerms(forms, query, {
    cap,
    getTerms: (form) => [form.name, form.form_type, CUSTOM_FORM_TYPE_LABELS[form.form_type] ?? ''],
  });
}

// ─── Reporting Webhooks ───────────────────────────────────────────────────────

/**
 * Maps a ReportingWebhook to a ReportingWebhookResult.
 * The edit URL follows the Journeys integrations/reporting-webhooks/:id route.
 */
export function mapReportingWebhookToSearchResult(
  webhook: ReportingWebhook,
  workspaceId: number,
): ReportingWebhookResult {
  return {
    kind: 'reporting_webhook',
    doc_id: `reporting_webhook-${webhook.id}`,
    title: webhook.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/reporting-webhooks/${webhook.id}`,
    other: {
      enabled: webhook.enabled,
      created_at: webhook.created_at > 0 ? webhook.created_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for reporting webhooks. Matches against name.
 */
export function filterReportingWebhooks(webhooks: ReportingWebhook[], query: string, cap: number): ReportingWebhook[] {
  return filterBySearchTerms(webhooks, query, {
    cap,
    getTerms: (webhook) => [webhook.name],
  });
}
